import { createGlobalKey } from "@aviv-unified-frontend/internal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { CustomBannerVariant } from "./variants/types";

export type BannerData = {
  dismiss: {
    count: number;
    at: number;
  };
};

export const DEFAULT_BANNER_DATA: BannerData = {
  dismiss: {
    count: 0,
    at: Date.now(),
  },
};

export const CUSTOM_BANNER_STORAGE_KEY =
  createGlobalKey("customBanner").toLowerCase();

type UseCustomBannerDataInput = {
  variant: CustomBannerVariant;
};

/**
 * Hook to manage banner data as state synchronized with localStorage.
 */
export const useCustomBannerData = ({ variant }: UseCustomBannerDataInput) => {
  const [bannerData, setBannerData] = useState<BannerData | undefined>(
    undefined,
  );

  const isLocalStorageUpdated = useRef(false);

  /**
   * Initialize banner data from local storage on mount
   */
  useEffect(() => {
    setBannerData(getLocalBannerData());
  }, []);

  const shouldShowBanner = useMemo(() => {
    return bannerData ? computeShouldShowBanner(bannerData, variant) : null;
  }, [bannerData, variant]);

  /***
   * Update the local banner data in local storage.
   * This function should be called when the user interacts with the banner (e.g., clicks a link or dismisses the banner).
   *
   * Update is done only once to avoid incrementing the count multiple times.
   */
  const onUpdateBannerData = useCallback(() => {
    if (!bannerData || isLocalStorageUpdated.current) return;

    const nextData = {
      dismiss: {
        count: bannerData.dismiss.count + 1,
        at: Date.now(),
      },
    };

    setLocalBannerData(nextData);
    isLocalStorageUpdated.current = true;
  }, [bannerData]);

  return {
    bannerData,
    onUpdateBannerData,
    shouldShowBanner,
  };
};

export const getLocalBannerData = (): BannerData => {
  const data = localStorage.getItem(CUSTOM_BANNER_STORAGE_KEY);

  try {
    return data ? JSON.parse(data) : DEFAULT_BANNER_DATA;
  } catch {
    return DEFAULT_BANNER_DATA;
  }
};

export const setLocalBannerData = (data: BannerData) => {
  localStorage.setItem(CUSTOM_BANNER_STORAGE_KEY, JSON.stringify(data));
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

/**
 * Mapping of hide durations based on banner variant and dismissal count.
 */
const VARIANT_HIDE_DURATIONS: Record<
  CustomBannerVariant,
  Record<number, number>
> = {
  bottom: {
    1: DAY_IN_MS,
    2: 2 * DAY_IN_MS,
    3: 3 * DAY_IN_MS,
    4: 7 * DAY_IN_MS,
  },
  persistent: {
    1: 7 * DAY_IN_MS,
    2: 7 * DAY_IN_MS,
    3: 7 * DAY_IN_MS,
    4: 7 * DAY_IN_MS,
  },
};

export const getHideDuration = (
  count: number,
  variant: CustomBannerVariant,
) => {
  const variantConfig = VARIANT_HIDE_DURATIONS[variant];

  return variantConfig[count] ?? null;
};

export const computeShouldShowBanner = (
  data: BannerData,
  variant: CustomBannerVariant,
): boolean => {
  const { at, count } = data.dismiss;

  if (count === 0) return true;

  const hideDuration = getHideDuration(count, variant);

  if (hideDuration === null) return false;

  return Date.now() - at >= hideDuration;
};
